import React, { useEffect, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Order + brand colors — matches the maintenance page.
const SOCIALS = [
  { key: 'messenger_url',  label: 'Messenger',  icon: 'fa-brands fa-facebook-messenger', color: '#00B2FF' },
  { key: 'facebook_url',   label: 'Facebook',   icon: 'fa-brands fa-facebook',           color: '#1877F2' },
  { key: 'instagram_url',  label: 'Instagram',  icon: 'fa-brands fa-instagram',          color: '#E1306C' },
  { key: 'tiktok_url',     label: 'TikTok',     icon: 'fa-brands fa-tiktok',             color: '#25F4EE' },
  { key: 'spotify_url',    label: 'Spotify',    icon: 'fa-brands fa-spotify',            color: '#1DB954' },
  { key: 'soundcloud_url', label: 'SoundCloud', icon: 'fa-brands fa-soundcloud',         color: '#FF5500' },
];

/**
 * SocialLinksRow — reads /api/settings once and renders whichever platforms
 * the admin has configured. Empty URLs are hidden automatically so this stays
 * self-managing.
 *
 * Props:
 *   - featured?: 'messenger' — renders the Messenger link as a big pill button
 *     above the icon row (used on the Contact page to steer visitors to DM).
 *   - compact?: boolean  — icon-only, tiny footprint (used in the site footer).
 *   - align?: 'center' | 'start'  (default 'center')
 */
const SocialLinksRow = ({ featured, compact = false, align = 'center' }) => {
  const [socials, setSocials] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/settings`)
      .then((r) => { if (!cancelled) setSocials(r.data || {}); })
      .catch(() => { if (!cancelled) setSocials({}); });
    return () => { cancelled = true; };
  }, []);

  if (!socials) return null;

  const visible = SOCIALS
    .map((s) => ({ ...s, url: (socials[s.key] || '').trim() }))
    .filter((s) => !!s.url);

  const alignClass = align === 'start' ? 'justify-start' : 'justify-center';

  const featuredEntry = featured
    ? visible.find((s) => s.key === `${featured}_url`)
    : null;
  const rest = featuredEntry
    ? visible.filter((s) => s.key !== featuredEntry.key)
    : visible;

  if (visible.length === 0) return null;

  // Compact — icon-only, used in the site footer.
  if (compact) {
    return (
      <div className="flex items-center gap-3" data-testid="social-links-row">
        {visible.map((s) => (
          <a
            key={s.key}
            href={s.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#A1A1AA] hover:text-white transition-colors"
            title={s.label}
            aria-label={s.label}
            data-testid={`social-link-${s.label.toLowerCase()}`}
          >
            <i className={s.icon} style={{ fontSize: 16 }} />
          </a>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="social-links-row">
      {featuredEntry && (
        <a
          href={featuredEntry.url}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full border border-[#27272A] bg-[#0F0F11] hover:border-[#00F0FF]/50 hover:bg-[#121216] transition-colors text-sm font-medium"
          data-testid={`social-featured-${featuredEntry.label.toLowerCase()}`}
        >
          <i className={featuredEntry.icon} style={{ color: featuredEntry.color, fontSize: 18 }} />
          Message me on {featuredEntry.label}
        </a>
      )}
      {rest.length > 0 && (
        <div className={`flex flex-wrap items-center gap-2 ${alignClass}`}>
          {rest.map((s) => (
            <a
              key={s.key}
              href={s.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#27272A] bg-[#0F0F11] hover:border-[#00F0FF]/40 hover:bg-[#121216] transition-colors text-xs"
              title={s.label}
              aria-label={s.label}
              data-testid={`social-link-${s.label.toLowerCase()}`}
            >
              <i className={s.icon} style={{ color: s.color, fontSize: 14 }} />
              <span className="hidden sm:inline text-[#D4D4D8]">{s.label}</span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
};

export default SocialLinksRow;
